import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Tooltip } from "react-tooltip";

import { ReactComponent as EditIcon } from "images/profile/edit-icon.svg";
import { ReactComponent as LockedIcon } from "images/profile/lockedIcon.svg";
import { ReactComponent as CheckIcon } from "images/profile/check-done.svg";
import LabelComponent from "pages/profile/common/label";
import RatesGroup from "./RatesGroup";
import {
  INTERPRETATION_SERVICE_NAME,
  LANGUAGE_MODEL_NONE,
  LANGUAGE_MODEL_SINGLE,
  DTP_SERVICE,
  LQE_REVIEW_SERVICE_NAME,
  categoryMapForInterpretation,
  orderMapForInterpretation,
} from "utils/constantValues";
import SelectComponent from "components/select";

import useServiceFilter from "../../hooks/useServiceFilter";
function Services({ lockService = false, isVendor = false, onEditClick }) {
  const [t] = useTranslation();
  const {
    serviceOptions,
    selectedStatuses,
    setSelectedStatuses,
    setFilterQuery,
    handleClear,
    rates,
    disabled,
  } = useServiceFilter(isVendor);
  return (
    <div
      className={`flex w-full flex-col items-end gap-5 p-6 flex-shrink-0 self-stretch `}
    >
      {/* title */}
      <div
        className={`${
          lockService
            ? "flex items-center gap-3 self-stretch"
            : "flex justify-between items-center self-stretch"
        } `}
      >
        <p className="text-[#1D1B22] text-center font-semibold text-[30px] leading-[40px] font-['General_Sans'] ">
          {isVendor ? t("skillAndRates") : t("serviceAndRate")}
        </p>
        {lockService && <LockedIcon className={` `} />}
        {!lockService && (
          <div className="flex flex-col items-start gap-3 self-stretch hover:cursor-pointer">
            <div className="flex justify-between items-center self-stretch hover:cursor-pointer">
              <EditIcon className={` `} onClick={onEditClick} />
            </div>
          </div>
        )}
      </div>
      {/* filter */}

      <div className="flex flex-wrap gap-[16px] self-stretch">
        {/* service Filter */}
        <div className="min-w-[200px]">
          <SelectComponent
            placeholder={t("Service")}
            options={serviceOptions}
            value={selectedStatuses}
            isDisabled={disabled}
            onChange={(values) => {
              setSelectedStatuses(values);
              setFilterQuery((prevQuery) => {
                const existing = prevQuery.filter.value.find(
                  (item) => item.key === "$serviceRateType.service_id$"
                );

                let value = prevQuery.filter.value;
                if (existing) {
                  value = prevQuery.filter.value.map((item) => {
                    if (item.key === "$serviceRateType.service_id$") {
                      item.value = values.map((s) => s.value);
                    }
                    return item;
                  });
                } else {
                  value.push({
                    op: "in",
                    key: "$serviceRateType.service_id$",
                    value: values.map((s) => s.value),
                  });
                }

                if (!values.length) {
                  value = value.filter(
                    (item) => item.key !== "$serviceRateType.service_id$"
                  );
                }

                return {
                  filter: { ...prevQuery.filter, value },
                };
              });
            }}
            isMulti
            withCheckBox
            closeMenuOnSelect={false}
            containerClassName="w-full"
            customInputStyle={`flex h-[44px] w-full items-center py-[8px] px-[14px] self-stretch rounded-[6px] border border-[#E4E4E7] ${
              disabled ? "bg-[#F4F4F5] cursor-not-allowed" : "bg-[#fff]"
            } outline-none text-[#1D1B22] font-regular text-[16px] not-italic`}
          />
        </div>

        {/* Clear Button */}
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className={`h-[40px] px-[14px] rounded-[6px] border border-[#E4E4E7] bg-white text-[#1D1B22] ${
            disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-[#F3F5F7]"
          }`}
        >
          {t("clearFilters")}
        </button>
      </div>
      {/* service */}
      <div className="flex flex-col justify-center items-start gap-[30px] self-stretch">
        <div className="flex flex-col items-start gap-3 self-stretch">
          <div className={`flex p-[24px] flex-col gap-[12px] self-stretch `}>
            {rates?.map((rate, index) => {
              const isCharacter =
                rate?.languageFrom?.isCharacter ??
                rate?.languageTo?.isCharacter ??
                false;

              const normalizeTitle = (title) =>
                title.toLowerCase().replace("-", " ").trim();

              const grouped = {
                online: [],
                onSite: [],
                rest: [],
              };

              rate.rateTypes.forEach((rt) => {
                const normalized = normalizeTitle(rt.title);
                const category = categoryMapForInterpretation[normalized];

                if (category === "online") grouped.online.push(rt);
                else if (category === "onSite") grouped.onSite.push(rt);
                else grouped.rest.push(rt);
              });

              const sortByOrder = (a, b) =>
                (orderMapForInterpretation[normalizeTitle(a.title)] ??
                  Infinity) -
                (orderMapForInterpretation[normalizeTitle(b.title)] ??
                  Infinity);

              grouped.online.sort(sortByOrder);
              grouped.onSite.sort(sortByOrder);
              grouped.rest.sort(sortByOrder);

              const online = grouped.online;
              const onSite = grouped.onSite;
              const restData = grouped.rest;

              return (
                <>
                  <div className="flex flex-col items-start gap-3 self-stretch">
                    <div className="flex justify-between items-center self-stretch">
                      <div className="flex items-center gap-2">
                        <span className="text-[#1D1B22] text-center font-semibold text-[24px] leading-[36px] font-['General_Sans']">
                          {t(`${rate.title}`)}
                        </span>
                        {rate.isVetted && (
                          <>
                            <CheckIcon
                              data-tooltip-id={`vetted-${rate.serviceId}-${index}`}
                              className="w-5 h-5 text-green-600 cursor-pointer"
                            />
                            <Tooltip
                              className="bg-base-black rounded-[8px] opacity-[1]"
                              place="top"
                              id={`vetted-${rate.serviceId}-${index}`}
                              content={t("vetted")}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {rate?.rateTypes?.length > 1 ? (
                    <div className="flex flex-col items-start content-start gap-[24px] self-stretch flex-wrap">
                      <div className="flex w-full flex-col items-start gap-[2px]">
                        {rate.languageModel !== LANGUAGE_MODEL_NONE && (
                          <LabelComponent
                            title={
                              rate.languageModel === LANGUAGE_MODEL_SINGLE
                                ? "To Language"
                                : "From/To Language"
                            }
                            subTitle={
                              rate.languageModel === LANGUAGE_MODEL_SINGLE
                                ? rate?.toLanguage?.title.toUpperCase() ?? ""
                                : `${rate.fromLanguage.title.toUpperCase()} > ${rate.toLanguage.title.toUpperCase()}`
                            }
                          />
                        )}
                        <div className="flex flex-col items-start content-start gap-6 self-stretch flex-wrap">
                          <RatesGroup
                            label={
                              rate?.title === INTERPRETATION_SERVICE_NAME
                                ? t("online")
                                : ""
                            }
                            rates={online}
                            key={index}
                            serviceTitle={rate?.title}
                            isCharacter={
                              rate?.languageFrom?.isCharacter ??
                              rate?.languageTo?.isCharacter
                            }
                            withCapacity={rate.withCapacity}
                            withRate={rate.withRate}
                          />
                          <RatesGroup
                            label={
                              rate?.title === INTERPRETATION_SERVICE_NAME
                                ? t("onsite")
                                : ""
                            }
                            rates={onSite}
                            key={index + 1}
                            serviceTitle={rate?.title}
                            isCharacter={
                              rate?.languageFrom?.isCharacter ??
                              rate?.languageTo?.isCharacter
                            }
                            withCapacity={rate.withCapacity}
                            withRate={rate.withRate}
                          />
                          <RatesGroup
                            label={""}
                            rates={restData}
                            key={index + 2}
                            serviceTitle={rate?.title}
                            isCharacter={
                              rate?.languageFrom?.isCharacter ??
                              rate?.languageTo?.isCharacter
                            }
                            withCapacity={rate.withCapacity}
                            withRate={rate.withRate}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-[20px]">
                      {rate.languageModel !== "NONE" && (
                        <LabelComponent
                          title={`From/To Language`}
                          subTitle={`${
                            rate?.fromLanguage?.title
                              ? rate?.fromLanguage?.title
                              : "N/A"
                          } > ${
                            rate?.toLanguage?.title
                              ? rate?.toLanguage?.title
                              : "N/A"
                          }`}
                          customStyleClassName={
                            "grid gap-[2px] min-w-[300px] sm-max:w-full"
                          }
                        />
                      )}

                      {rate.withRate && (
                        <LabelComponent
                          title={t(`rate`)}
                          subTitle={`$${rate?.rateTypes[0]?.rate} ${rate?.rateTypes[0].title}`}
                          customStyleClassName={
                            "grid gap-[2px] min-w-[300px] sm-max:w-full"
                          }
                        />
                      )}
                      {rate.withCapacity && (
                        <LabelComponent
                          title={t(`capacity`)}
                          subTitle={(() => {
                            const rt = rate?.rateTypes?.[0];
                            const capacity = rt?.capacity;
                            const titleLower = (rt?.title || "")
                              .toLowerCase()
                              .trim();
                            const noun = titleLower
                              .replace(/^per\s+/, "")
                              .replace(/s$/, "");
                            let unit;
                            if (noun === "word") {
                              unit = isCharacter
                                ? `${t("character").toLowerCase()}s`
                                : t("words").toLowerCase();
                            } else {
                              unit = noun ? `${noun}s` : "";
                            }
                            return `${capacity} ${unit} ${t("perDay")}`;
                          })()}
                          customStyleClassName={
                            "grid gap-[2px] min-w-[300px] sm-max:w-full"
                          }
                        />
                      )}
                    </div>
                  )}
                  <div className="w-full h-px bg-[#E4E4E7]"></div>
                </>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

Services.propTypes = {
  onEditClick: PropTypes.func,
  lockService: PropTypes.bool,
  isVendor: PropTypes.bool,
};
export default Services;
